import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Send, User, Bot, CheckCircle, ExternalLink, RefreshCw, MessageCircle,
         Search, UserPlus, Loader2, Trash2, Zap } from 'lucide-react';
import { BotonesLlamar } from './Softphone';
import { FichaDesdeAnuncio } from './FichaDesdeAnuncio';
import { MensajeWhatsapp } from './MensajeWhatsapp';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// La sección Idealista: lo que el setter ha hecho ahí fuera.
//
// El agente vive en un navegador FUERA del servidor (Idealista bloquea las IPs de
// centro de datos), así que esta pantalla no "hace" cosas en Idealista: enseña lo
// que ya pasó y deja preparado lo que saldrá en la próxima pasada. Por eso un
// mensaje escrito a mano dice "se enviará", no "enviado": prometer un envío que
// todavía no ha salido sería mentirle al usuario.

const EUR = (n) => (n == null ? '—' : new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n));

const cuando = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const min = Math.round((Date.now() - d) / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  if (min < 1440) return `hace ${Math.round(min / 60)} h`;
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

const COLOR_ESTADO = {
  localizado: '#64748b', contactado: '#c9a84c', respondido: '#06b6d4',
  convertido: '#10b981', descartado: '#ef4444',
  enviado: '#c9a84c', en_curso: '#10b981', cerrado: '#ef4444', fallido: '#ef4444',
};

export function SeccionIdealista({ orgId }) {
  const [estado, setEstado] = useState(null);
  const [chats, setChats] = useState([]);
  const [anuncios, setAnuncios] = useState([]);
  const [abierto, setAbierto] = useState(null);   // { chat, mensajes, anuncio }
  const [vista, setVista] = useState('anuncios'); // anuncios | chats
  const [texto, setTexto] = useState('');
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [ocupado, setOcupado] = useState('');
  const [fichando, setFichando] = useState(null);   // el anuncio que se pasa a ficha
  const [escribiendo, setEscribiendo] = useState(null);
  const [runner, setRunner] = useState(null);   // el rastreador del PC de Marcos // el anuncio al que se le manda WhatsApp       // qué botón está trabajando

  const cargar = useCallback(async () => {
    if (!orgId) return;
    try {
      const [e, c, a, r] = await Promise.all([
        axios.get(`${API}/crm/idealista/status?org_id=${orgId}`),
        axios.get(`${API}/crm/idealista/chats?org_id=${orgId}`),
        axios.get(`${API}/crm/idealista/listings?org_id=${orgId}`),
        // Si el rastreador del PC nunca ha dado señales esto viene vacío; no es
        // un error, es "el ordenador está apagado".
        axios.get(`${API}/crm/idealista/runner?org_id=${orgId}`).catch(() => ({ data: null })),
      ]);
      setEstado(e.data); setChats(c.data || []); setAnuncios(a.data || []);
      setRunner(r.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar Idealista');
    }
    setCargando(false);
  }, [orgId]);



  // Fotocasa y pisos.com sí se dejan leer desde el servidor, y además publican
  // el teléfono del particular. Esto no necesita el rastreador del PC.
  const buscarEnPortales = async () => {
    setOcupado('portales');
    try {
      const r = await axios.post(`${API}/crm/portales/rastrear`,
        { org_id: orgId, objetivo: 15, solo_con_telefono: true });
      const d = r.data || {};
      toast.success(d.nuevos
        ? `${d.nuevos} propietarios nuevos con teléfono (${d.particulares} particulares vistos)`
        : `Sin novedades: los ${d.repetidos} particulares con teléfono ya los tenías`);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo buscar en los portales');
    }
    setOcupado('');
  };

  // Idealista NO se puede leer desde el servidor: DataDome bloquea las IP de
  // centro de datos. Este botón no rastrea, deja una ORDEN en la cola; el
  // rastreador del PC la recoge en unos segundos y avisa cuando termina.
  //
  // Va en modo "solo rastrear" a propósito: trae los anuncios y para. Escribir
  // a los propietarios es otra decisión y no debe salir de un botón que uno
  // pulsa para ver qué hay.
  const rastrearIdealista = async () => {
    setOcupado('idealista');
    try {
      const r = await axios.post(`${API}/crm/idealista/runner/ejecutar`,
        { org_id: orgId, objetivo: 10, solo_rastrear: true });
      toast.success(r.data?.aviso || 'Orden enviada al rastreador.');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo lanzar el rastreo');
    }
    setOcupado('');
  };

  const pasarAVendedor = async () => {
    if (!abierto) return;
    setOcupado('vendedor');
    try {
      const r = await axios.post(
        `${API}/crm/idealista/chats/${abierto.chat.chat_id}/a-vendedor`,
        { org_id: orgId, crear_propiedad: true });
      toast.success(r.data?.ya_existia
        ? 'Ese propietario ya tenía ficha; se ha enlazado con ella.'
        : 'Ficha de vendedor y propiedad creadas.');
      cargar();
      abrirChat(abierto.chat.chat_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo pasar a vendedor');
    }
    setOcupado('');
  };

  useEffect(() => { cargar(); }, [cargar]);
  // Refresco suave: las respuestas llegan cuando el agente hace su pasada, no al
  // segundo. Cada 60 s es más que suficiente y no castiga al backend.
  // Refresco suave: esto no cambia solo, cambia cuando alguien le da al boton
  // o cuando entra el cron de la mañana.
  useEffect(() => {
    const t = setInterval(cargar, 60000);
    return () => clearInterval(t);
  }, [cargar]);

  const abrirChat = async (chatId) => {
    try {
      const r = await axios.get(`${API}/crm/idealista/chats/${chatId}`);
      setAbierto(r.data);
      setChats((prev) => prev.map((c) => (c.chat_id === chatId ? { ...c, no_leidos: 0 } : c)));
    } catch (err) { toast.error('No se pudo abrir el chat'); }
  };

  const cambiarModo = async (chatId, modo) => {
    try {
      await axios.put(`${API}/crm/idealista/chats/${chatId}/mode`, { modo });
      toast.success(modo === 'humano' ? 'La IA ya no responde en este chat' : 'La IA vuelve a llevar el chat');
      setAbierto((p) => (p ? { ...p, chat: { ...p.chat, modo } } : p));
      cargar();
    } catch (err) { toast.error('No se pudo cambiar el modo'); }
  };

  const encolar = async () => {
    const t = texto.trim();
    if (!t || !abierto) return;
    setEnviando(true);
    try {
      const r = await axios.post(`${API}/crm/idealista/chats/${abierto.chat.chat_id}/messages`, { texto: t });
      toast.success(r.data?.aviso || 'Encolado');
      setTexto('');
      abrirChat(abierto.chat.chat_id);
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo dejar el mensaje'); }
    setEnviando(false);
  };


  // Quitar un anuncio de la lista. Si ya tiene ficha de vendedor, la ficha se
  // queda: se borra el papel, no el propietario.
  const borrarAnuncio = async (a) => {
    const que = a.titulo || a.idealista_id || 'este anuncio';
    const aviso = a.seller_id
      ? '\n\nSu ficha de vendedor NO se borra.'
      : '';
    if (!window.confirm(`Quitar de la lista "${que}"?${aviso}`)) return;
    try {
      await axios.delete(`${API}/crm/portales/listings/${a.listing_id}?org_id=${orgId}`);
      toast.success('Anuncio eliminado');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  if (cargando) return <div className="p-8 text-white/40 text-sm">Cargando…</div>;

  const res = estado?.anuncios || {};

  return (
    <div className="space-y-5">
      {/* Fotocasa y pisos.com: esto SÍ corre en el servidor y no depende de
          ningún ordenador encendido. Además traen el teléfono del particular,
          así que de aquí se sale llamando, no escribiendo. */}
      <div className="rounded-xl border p-4"
           style={{ background: 'rgba(16,185,129,0.06)', borderColor: 'rgba(16,185,129,0.25)' }}>
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0">
            <p className="text-white/40 text-[11px] uppercase tracking-wide">Fotocasa y pisos.com</p>
            <p className="text-white/70 text-sm mt-0.5">
              Particulares con <span className="text-emerald-300">teléfono directo</span>, en el
              noreste de Madrid. Corre en el servidor: no hace falta tener nada encendido.
            </p>
            <p className="text-white/30 text-[11px] mt-1">
              Se priorizan los que más tiempo llevan en venta, que son los que mejor escuchan.
            </p>
          </div>
          <button onClick={buscarEnPortales} disabled={!!ocupado}
            className="ml-auto px-4 py-2 rounded-lg text-[11px] font-bold bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-40 flex items-center gap-1.5">
            {ocupado === 'portales' ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
            Buscar propietarios ahora
          </button>
        </div>
      </div>

      {/* Idealista: aquí SÍ hace falta el ordenador de Marcos encendido. */}
      <div className="rounded-xl border p-4"
        style={{ background: 'rgba(201,168,76,0.06)', borderColor: 'rgba(201,168,76,0.25)' }}>
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0">
            <p className="text-white/40 text-[11px] uppercase tracking-wide flex items-center gap-2">
              Idealista
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold"
                style={{ color: runner?.conectado ? '#10b981' : '#ef4444' }}>
                <span className="w-1.5 h-1.5 rounded-full"
                  style={{ background: runner?.conectado ? '#10b981' : '#ef4444' }} />
                {runner?.conectado ? 'rastreador conectado' : 'rastreador apagado'}
              </span>
            </p>
            <p className="text-white/70 text-sm mt-0.5">
              Idealista bloquea al servidor, así que rastrea{' '}
              <span className="text-[#c9a84c]">tu ordenador</span>. El botón deja la orden;
              el rastreador la recoge en segundos.
            </p>
            <p className="text-white/30 text-[11px] mt-1">
              Barre el listado sin abrir anuncio por anuncio y descarta de un vistazo
              los que llevan logo de inmobiliaria.
              {runner?.ultima?.terminado ? ` Última pasada ${cuando(runner.ultima.terminado)}.` : ''}
            </p>
          </div>
          <button onClick={rastrearIdealista} disabled={!!ocupado}
            className="ml-auto px-4 py-2 rounded-lg text-[11px] font-bold bg-[#c9a84c]/20 text-[#c9a84c] hover:bg-[#c9a84c]/30 disabled:opacity-40 flex items-center gap-1.5">
            {ocupado === 'idealista' ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
            Rastrear Idealista ahora
          </button>
        </div>
        {!runner?.conectado && (
          <p className="text-[11px] mt-3 pt-3 border-t border-white/8" style={{ color: '#f59e0b' }}>
            El rastreador no está dando señales. Puedes dejar la orden igualmente:
            se quedará esperando a que enciendas el ordenador.
          </p>
        )}
      </div>

      {/* El marcador: cuantos propietarios hay y en que punto estan */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ['Por contactar', res.por_contactar, '#c9a84c'],
          ['Contactados', res.contactados, '#06b6d4'],
          ['Han respondido', res.respondidos, '#8b5cf6'],
          ['Convertidos', res.convertidos, '#10b981'],
          ['Descartados', res.descartados, '#64748b'],
        ].map(([lbl, val, col]) => (
          <div key={lbl} className="rounded-xl border border-white/8 bg-white/4 p-4">
            <p className="text-white/40 text-[11px] uppercase tracking-wide">{lbl}</p>
            <p className="text-2xl font-black mt-1" style={{ color: col }}>{val ?? 0}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        {[['anuncios', `Propietarios (${anuncios.length})`], ['chats', `Conversaciones (${chats.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setVista(k)}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${vista === k ? 'bg-[#c9a84c] text-black' : 'text-white/50 hover:text-white bg-white/4'}`}>
            {l}
          </button>
        ))}
        <button onClick={cargar} className="ml-auto p-2 rounded-lg text-white/40 hover:text-white transition-colors" title="Actualizar">
          <RefreshCw size={14} />
        </button>
      </div>

      {vista === 'anuncios' ? (
        // overflow-x-auto: en el movil la tabla no cabe y sin esto se comia la
        // ultima columna (a Marcos se le ocultaba la "S" de llamar como Sergio).
        <div className="rounded-xl border border-white/8 overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead className="bg-white/4 text-white/40 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left p-3">Vivienda</th>
                <th className="text-left p-3">Portal</th>
                <th className="text-left p-3">Zona</th>
                <th className="text-right p-3">Precio</th><th className="text-right p-3">Días</th>
                <th className="text-left p-3 whitespace-nowrap">Contactar</th>
                <th className="text-left p-3">Estado</th>
                <th className="text-right p-3">Ficha</th>
              </tr>
            </thead>
            <tbody>
              {anuncios.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-white/30 text-xs">
                  Todavía no hay propietarios. Dale a "Buscar propietarios ahora".
                </td></tr>
              )}
              {anuncios.map((a) => (
                <tr key={a.listing_id} className="border-t border-white/5 hover:bg-white/2">
                  <td className="p-3 text-white/80">
                    {a.url ? <a href={a.url} target="_blank" rel="noreferrer" className="hover:text-[#c9a84c] flex items-center gap-1">
                      {a.titulo || a.idealista_id} <ExternalLink size={11} className="opacity-40" />
                    </a> : (a.titulo || a.idealista_id)}
                    {a.anunciante_tipo !== 'particular' && <span className="ml-2 text-[10px] text-white/30">(no particular)</span>}
                    {a.anunciante_nombre && a.anunciante_nombre !== '-' && (
                      <span className="ml-2 text-[10px] text-white/40">· {a.anunciante_nombre}</span>
                    )}
                  </td>
                  <td className="p-3">
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold whitespace-nowrap"
                          style={{
                            background: a.origen === 'fotocasa' ? 'rgba(16,185,129,0.15)'
                                      : a.origen === 'pisos.com' ? 'rgba(99,102,241,0.15)'
                                      : 'rgba(255,255,255,0.06)',
                            color: a.origen === 'fotocasa' ? '#10b981'
                                 : a.origen === 'pisos.com' ? '#818cf8' : '#94a3b8',
                          }}>
                      {a.origen || 'idealista'}
                    </span>
                  </td>
                  <td className="p-3 text-white/50">{a.zona || a.municipio || '—'}</td>
                  <td className="p-3 text-right text-white/70">{EUR(a.precio)}</td>
                  <td className="p-3 text-right text-white/50">{a.dias_en_mercado ?? '—'}</td>
                  <td className="p-3">
                    {/* Con el teléfono delante, de aquí se sale llamando. */}
                    {a.datos_crudos?.telefono ? (
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <BotonesLlamar telefono={a.datos_crudos.telefono}
                                       nombre={a.anunciante_nombre || a.titulo}
                                       listingId={a.listing_id} compacto />
                        <button onClick={() => setEscribiendo(a)}
                          title="Escribirle el primer WhatsApp"
                          className="w-[26px] h-[26px] rounded-full flex items-center justify-center border border-emerald-500/35 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20">
                          <MessageCircle size={12} />
                        </button>
                      </div>
                    ) : <span className="text-white/25">—</span>}
                  </td>
                  <td className="p-3">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                          style={{ background: `${COLOR_ESTADO[a.estado]}22`, color: COLOR_ESTADO[a.estado] }}>
                      {a.estado}
                    </span>
                    {a.motivo_descarte && <span className="block text-white/25 text-[10px] mt-0.5">{a.motivo_descarte}</span>}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => borrarAnuncio(a)} title="Quitar este anuncio de la lista"
                      className="text-white/25 hover:text-red-400 transition-colors p-1 mr-1 align-middle">
                      <Trash2 size={12} />
                    </button>
                    {a.seller_id ? (
                      <span className="text-emerald-400/70 text-[11px] flex items-center gap-1 justify-end">
                        <CheckCircle size={11} /> ya es vendedor
                      </span>
                    ) : (
                      <button onClick={() => setFichando(a)}
                        className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 flex items-center gap-1 ml-auto">
                        <UserPlus size={11} /> A vendedor
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid md:grid-cols-[340px_1fr] gap-4">
          {/* Lista de conversaciones */}
          <div className="rounded-xl border border-white/8 overflow-hidden max-h-[560px] overflow-y-auto">
            {chats.length === 0 && <p className="p-6 text-white/30 text-xs">Aún no hay conversaciones abiertas.</p>}
            {chats.map((c) => (
              <button key={c.chat_id} onClick={() => abrirChat(c.chat_id)}
                className={`w-full text-left p-3 border-b border-white/5 transition-colors ${abierto?.chat?.chat_id === c.chat_id ? 'bg-white/8' : 'hover:bg-white/4'}`}>
                <div className="flex items-center gap-2">
                  <span className="text-white/85 text-sm font-semibold truncate">{c.vendedor_nombre || 'Sin nombre'}</span>
                  {c.no_leidos > 0 && <span className="ml-auto px-1.5 py-0.5 rounded-full bg-[#c9a84c] text-black text-[10px] font-bold">{c.no_leidos}</span>}
                </div>
                <p className="text-white/40 text-[11px] truncate mt-0.5">{c.anuncio_titulo || '—'}</p>
                <p className="text-white/30 text-[11px] truncate mt-1">{c.ultimo_mensaje}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold"
                        style={{ background: `${COLOR_ESTADO[c.estado]}22`, color: COLOR_ESTADO[c.estado] }}>{c.estado}</span>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${c.modo === 'ia' ? 'bg-cyan-500/15 text-cyan-300' : 'bg-white/10 text-white/50'}`}>
                    {c.modo === 'ia' ? 'IA' : 'humano'}
                  </span>
                  <span className="ml-auto text-white/25 text-[10px]">{cuando(c.ultimo_mensaje_at)}</span>
                </div>
              </button>
            ))}
          </div>

          {/* La conversación */}
          <div className="rounded-xl border border-white/8 flex flex-col min-h-[400px] max-h-[560px]">
            {!abierto ? (
              <p className="m-auto text-white/25 text-sm">Elige una conversación</p>
            ) : (
              <>
                <div className="p-3 border-b border-white/8 flex items-center gap-3">
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{abierto.chat.vendedor_nombre || 'Sin nombre'}</p>
                    <p className="text-white/40 text-[11px] truncate">
                      {abierto.anuncio?.titulo}
                      {abierto.anuncio?.precio ? ` · ${EUR(abierto.anuncio.precio)}` : ''}
                      {abierto.anuncio?.zona ? ` · ${abierto.anuncio.zona}` : ''}
                    </p>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    {abierto.chat.anuncio_url && (
                      <a href={abierto.chat.anuncio_url} target="_blank" rel="noreferrer"
                         className="text-white/40 hover:text-[#c9a84c] p-1.5" title="Ver el anuncio"><ExternalLink size={14} /></a>
                    )}
                    {abierto.chat.estado !== 'convertido' && (
                      <button onClick={pasarAVendedor} disabled={!!ocupado}
                        className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-40 flex items-center gap-1.5"
                        title="Abrir ficha de vendedor y propiedad con los datos de este anuncio">
                        {ocupado === 'vendedor' ? <Loader2 size={11} className="animate-spin" /> : <UserPlus size={11} />}
                        Pasar a vendedor
                      </button>
                    )}
                    {abierto.chat.estado === 'convertido' && (
                      <span className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-emerald-500/10 text-emerald-400/70 flex items-center gap-1.5">
                        <CheckCircle size={11} /> Ya es vendedor
                      </span>
                    )}
                    <button onClick={() => cambiarModo(abierto.chat.chat_id, abierto.chat.modo === 'ia' ? 'humano' : 'ia')}
                      className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                        abierto.chat.modo === 'ia' ? 'bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25' : 'bg-white/10 text-white/60 hover:bg-white/15'}`}>
                      {abierto.chat.modo === 'ia' ? <><Bot size={11} className="inline mr-1" />Lo lleva la IA</> : <><User size={11} className="inline mr-1" />Lo llevas tú</>}
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {(abierto.mensajes || []).map((m) => {
                    const mio = m.direction === 'saliente';
                    return (
                      <div key={m.message_id} className={`flex ${mio ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[78%] rounded-xl px-3 py-2 ${mio ? 'bg-[#c9a84c]/12 border border-[#c9a84c]/25' : 'bg-white/6 border border-white/8'}`}>
                          <p className="text-white/85 text-sm whitespace-pre-wrap">{m.texto}</p>
                          <p className="text-white/30 text-[10px] mt-1 flex items-center gap-1.5">
                            {m.emisor_nombre || (mio ? 'Marcos' : 'Vendedor')}
                            {m.emisor_tipo === 'ia' && <span className="text-cyan-400/70">· IA</span>}
                            <span>· {cuando(m.timestamp)}</span>
                            {mio && !m.enviado_el && <span className="text-amber-300/80">· se enviará en la próxima pasada</span>}
                            {m.error && <span className="text-red-400/80">· {m.error}</span>}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="p-3 border-t border-white/8">
                  <div className="flex gap-2">
                    <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={2}
                      placeholder="Escribe tú al vendedor. Sale en la próxima pasada del agente."
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/85 placeholder-white/25 resize-none focus:outline-none focus:border-[#c9a84c]/50" />
                    <motion.button whileTap={{ scale: 0.95 }} onClick={encolar} disabled={enviando || !texto.trim()}
                      className="px-4 rounded-lg bg-[#c9a84c] text-black font-bold text-sm disabled:opacity-30">
                      <Send size={15} />
                    </motion.button>
                  </div>
                  <p className="text-white/25 text-[10px] mt-1.5">
                    Al escribir tú, el chat pasa a modo humano y la IA deja de contestar.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Pasar un anuncio a ficha de vendedor. Se abre con lo que se pudo
          scrapear y se completa a mano lo que falte: el scraping nunca trae el
          nombre del dueño ni su situacion, y eso es justo lo que hace util la
          ficha. */}
      {fichando && (
        <FichaDesdeAnuncio
          anuncio={fichando}
          orgId={orgId}
          onCerrar={() => setFichando(null)}
          onHecho={() => { setFichando(null); cargar(); }}
        />
      )}

      {escribiendo && (
        <MensajeWhatsapp
          anuncio={escribiendo}
          orgId={orgId}
          onCerrar={() => setEscribiendo(null)}
          onEnviado={() => { setEscribiendo(null); cargar(); }}
        />
      )}

      {/* Últimas pasadas del agente */}
      {estado?.ultimas_pasadas?.length > 0 && (
        <div className="rounded-xl border border-white/8 p-4">
          <p className="text-white/40 text-[11px] uppercase tracking-wide mb-3">Últimas pasadas del agente</p>
          <div className="space-y-1.5">
            {estado.ultimas_pasadas.slice(0, 6).map((p) => (
              <div key={p.run_id} className="flex items-center gap-3 text-xs">
                <span className="text-white/50 w-32">{new Date(p.empezado_el).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                <span className="text-white/40">{p.anuncios_vistos ?? 0} vistos · {p.particulares ?? 0} particulares · {p.mensajes_enviados ?? 0} enviados</span>
                {p.error && <span className="text-red-400/80 truncate">{p.error}</span>}
                <span className="ml-auto text-white/30">{p.estado}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default SeccionIdealista;
